import {
  CheckCheck,
  MessagesSquare,
  Package,
  PackageCheck,
  Palette,
  Play,
  Printer,
  Scissors,
  Shirt,
} from 'lucide-react'
import { type ReactNode } from 'react'

import type {
  ProductionOrderSummaryType,
  UNIFORM_ART_STATUS_VALUES,
  UNIFORM_FUNCTION_VALUES,
  UNIFORM_LOGISTICS_VALUES,
  UNIFORM_PAYMENT_MARK_VALUES,
  UNIFORM_STAGE_VALUES,
} from '@aeris/contracts'
import { type StatusTone } from '@aeris/ui'

/**
 * O que cada etapa da fila de produção QUER DIZER — F170.
 *
 * O molde é `sales-status-meta.tsx` (F156): rótulo, tom, ícone e a frase que
 * explica moram juntos, e o selo da fila, a legenda e o histórico da ficha leem
 * daqui. Os rótulos são os da planilha da loja — "Iniciar impressão",
 * "Costurando" — porque é a palavra que a produção já fala.
 *
 * O `Badge` tem cinco tons e a fila tem nove etapas: o ícone e a palavra é que
 * distinguem, e o tom diz só o grande estado — esperando, na máquina, na
 * costura, pronto.
 *
 * Só constantes, nenhum componente — fronteira de Fast Refresh.
 */

export type UniformStage = (typeof UNIFORM_STAGE_VALUES)[number]
export type UniformLogistics = (typeof UNIFORM_LOGISTICS_VALUES)[number]
export type UniformPaymentMark = (typeof UNIFORM_PAYMENT_MARK_VALUES)[number]
export type UniformArtStatus = (typeof UNIFORM_ART_STATUS_VALUES)[number]
export type UniformFunction = (typeof UNIFORM_FUNCTION_VALUES)[number]

export type StageMeta = {
  readonly label: string
  readonly tone: StatusTone
  readonly icon: ReactNode
  readonly what: string
}

const icon = (Glyph: typeof Palette): ReactNode => (
  <Glyph aria-hidden="true" className="h-3 w-3" />
)

export const STAGE_META: Record<UniformStage, StageMeta> = {
  atendimento: {
    label: 'Atendimento',
    tone: 'neutral',
    icon: icon(MessagesSquare),
    what: 'A atendente conversando com o cliente. Ela diz quando vai para a arte.',
  },
  arte: {
    label: 'Arte',
    tone: 'neutral',
    icon: icon(Palette),
    what: 'Arte, Corel e grade ainda sendo fechados com o cliente.',
  },
  'iniciar-impressao': {
    label: 'Iniciar impressão',
    tone: 'warning',
    icon: icon(Play),
    what: 'Tudo pronto para ir para a máquina.',
  },
  imprimindo: {
    label: 'Imprimindo',
    tone: 'info',
    icon: icon(Printer),
    what: 'Na impressora — a ficha diz em qual.',
  },
  impresso: {
    label: 'Impresso',
    tone: 'info',
    icon: icon(CheckCheck),
    what: 'Saiu da impressora e espera o corte.',
  },
  cortando: {
    label: 'Cortando',
    tone: 'info',
    icon: icon(Scissors),
    what: 'No corte.',
  },
  costurando: {
    label: 'Costurando',
    tone: 'info',
    icon: icon(Shirt),
    what: 'Na costura.',
  },
  embalando: {
    label: 'Embalando',
    tone: 'warning',
    icon: icon(Package),
    what: 'Embalando para sair.',
  },
  pronto: {
    label: 'Pronto',
    tone: 'success',
    icon: icon(PackageCheck),
    what: 'Pronto para despachar ou para o cliente retirar. Sai da fila quando sair da loja.',
  },
}

export const LOGISTICS_LABELS: Record<UniformLogistics, string> = {
  correios: 'Correios',
  van: 'Van',
  uber: 'Uber',
  retirada: 'Retirada',
}

/**
 * A marcação de pagamento — ANOTAÇÃO, não dinheiro.
 *
 * É a coluna "Sinal pago?" da planilha. Desde a F173 o sinal é dinheiro de
 * verdade, recebido pelo caixa, e desconta na retirada e na entrega; isto
 * ficou para a Cortesia e para as anotações gravadas antes.
 */
export const PAYMENT_MARK_META: Record<
  UniformPaymentMark,
  { readonly label: string; readonly tone: StatusTone }
> = {
  nao: { label: 'Não', tone: 'danger' },
  sinal: { label: 'Sinal', tone: 'warning' },
  total: { label: '100%', tone: 'success' },
  cortesia: { label: 'Cortesia', tone: 'neutral' },
}

/** "Uber ou retirada" — como a planilha escreve quando ainda não decidiram. */
export const logisticsText = (
  logistics: ProductionOrderSummaryType['logistics']
): string =>
  logistics.length === 0
    ? '—'
    : logistics
        .map((value, index) =>
          index === 0
            ? LOGISTICS_LABELS[value]
            : LOGISTICS_LABELS[value].toLowerCase()
        )
        .join(' ou ')

/**
 * O andamento da arte (F171) — a conversa do designer com o cliente.
 *
 * `aprovada` é o que libera a impressão; a tela e o servidor dizem isso com a
 * mesma palavra.
 */
export const ART_STATUS_META: Record<
  UniformArtStatus,
  { readonly label: string; readonly tone: StatusTone; readonly what: string }
> = {
  aguardando: {
    label: 'Aguardando',
    tone: 'neutral',
    what: 'Ninguém começou a arte ainda.',
  },
  criando: {
    label: 'Criando',
    tone: 'info',
    what: 'O designer está fazendo a arte.',
  },
  enviada: {
    label: 'Com o cliente',
    tone: 'warning',
    what: 'A arte foi mandada para o cliente aprovar. Quem atende cobra a resposta.',
  },
  ajuste: {
    label: 'Ajuste pedido',
    tone: 'danger',
    what: 'O cliente pediu mudança na arte.',
  },
  aprovada: {
    label: 'Aprovada',
    tone: 'success',
    what: 'O cliente aprovou. O pedido pode ir para a impressão.',
  },
}

/**
 * O nome da função na tela (F171). O código é o SETOR (`atendimento`); a
 * pessoa é chamada pelo que faz — "Atendente", "Designer", "Operador".
 */
export const FUNCTION_LABELS: Record<
  UniformFunction,
  { readonly person: string; readonly sector: string }
> = {
  atendimento: { person: 'Atendente', sector: 'Atendimento' },
  arte: { person: 'Designer', sector: 'Arte' },
  producao: { person: 'Operador', sector: 'Produção' },
}
